import type { ComponentType, ReactNode } from 'react';

/**
 * Identity-provider families the app knows how to mount.
 */
export enum EAuthProviderType {
  /** Auth0, the only provider reachable today. */
  AUTH0 = 'auth0',
  /** A customer-owned OIDC provider (Microsoft Entra ID, Okta, generic OIDC). */
  OIDC = 'oidc',
  /** No provider resolved. Not a valid `authProviders` entry type. */
  NONE = 'none',
  /** The E2E identity stub. Not a valid `authProviders` entry type. */
  E2E = 'e2e',
}

/**
 * One entry of the `authProviders` array published in the runtime config.
 *
 * Only `key` and `type` are guaranteed; the remaining fields depend on the
 * provider family, so every consumer must treat them as optional.
 */
export interface IAuthProviderConfig {
  /** Stable identifier used in `/callback/:providerKey` and in stored state. */
  key: string;
  type: EAuthProviderType;
  /** Issuer URL, for OIDC providers. */
  issuer?: string;
  clientId?: string;
  audience?: string;
  scope?: string;
  /** Human-readable label for provider-picker UI. */
  displayName?: string;
}

/**
 * Props every adapter's provider component accepts.
 */
export interface IAuthAdapterProviderProps {
  children: ReactNode;
}

/**
 * The seam between the app and whichever identity provider is in play.
 *
 * `useAuth` stays the app's only auth surface; an adapter's job is to mount the
 * context that backs it.
 */
export interface IAuthAdapter {
  /** Matches the `key` of the runtime `authProviders` entry this was built from. */
  readonly key: string;
  readonly type: EAuthProviderType;
  /**
   * Mounts the session context `useAuth` reads.
   *
   * Must be a stable component reference: a fresh identity on every render
   * would remount the whole app below it.
   */
  readonly Provider: ComponentType<IAuthAdapterProviderProps>;
  /**
   * Reports whether the provider has everything it needs to start a login.
   * Never throws — it is diagnostic only.
   *
   * @returns {boolean} true when the configuration is complete
   */
  validateConfig(): boolean;
}
